import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Divider,
  Stack,
  FormControl,
  Select,
  Tooltip,
  IconButton,
  Chip,
  InputLabel,
  FormHelperText,
  Link,
  CircularProgress,
  Radio,
  Checkbox,
  FormControlLabel,
  Switch,
  Popover,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useNavigate } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { Space } from '../../models/Space';
import MediaUploader from '../../components/MediaUploader';
import { RoomGear } from '../../models/RoomGear';
import { RoomAmenity } from '../../models/RoomAmenity';
import { CreateRoomRequest } from '../../models/Room';
import { getAmenityLabel, amenityIcons } from '../../amenities';
import { getGearLabel, gearIcons } from '../../gears';
import { getCategoryLabel, categoryIcons } from '../../categories';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AmountInput from '../../components/AmountInput';
import { GoogleCalendarOption } from '../../models/GoogleCalendar';
import { ORGANIZATION_GOOGLE_CALENDAR_HREF, GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED } from '../../utils/organizationSections';
import { ROOM_CALENDAR_COLOR_OPTIONS } from '../../utils/calendarColors';

const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  PLN: 'zł',
  GBP: '£',
  AUD: 'A$',
};

const CreateRoomPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const [editValues, setEditValues] = useState<CreateRoomRequest>({
    spaceId: '',
    name: '',
    description: '',
    imageUrls: [],
    amenities: [],
    gears: [],
    pricePerH: 0,
    area: 0,      
    areaUnit: 'm2',    // default
    googleCalendarInfo: undefined,
    categories: [],
    primaryCategory: undefined,
    color: '#1E88E5', // default niebieski (blue) color
    visible: true,
  });

  const [newGear, setNewGear] = useState<RoomGear>({ gearType: '', details: '', pricePerH: 0 });
  const [newAmenity, setNewAmenity] = useState<RoomAmenity>({ amenityType: '', details: '', pricePerH: 0 });
  const selectedSpace = spaces.find((s) => String(s.id) === editValues.spaceId);
  const currencyCode = selectedSpace?.currencyCode || '';
  const [searchParams] = useSearchParams();
  const spaceIdFromUrl = searchParams.get('spaceId');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [googleCalendarOptions, setGoogleCalendarOptions] = useState<GoogleCalendarOption[]>([]);
  const [googleCalendarOptionsLoading, setGoogleCalendarOptionsLoading] = useState(false);
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        const data = await hostApiClient.getSpaces();
        setSpaces(data);
        
        if (GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED) {
          try {
            setGoogleCalendarOptionsLoading(true);
            const options = await hostApiClient.getGoogleCalendarOptions();
            setGoogleCalendarOptions(options);
          } catch (err) {
            console.error('Failed to fetch Google Calendar options:', err);
          } finally {
            setGoogleCalendarOptionsLoading(false);
          }
        } else {
          setGoogleCalendarOptions([]);
        }
      } catch (err) {
        console.error(t('room.failedToLoadSpaces', 'Failed to load spaces'), err);
      }
    };

    fetchSpaces();
  }, [t]);
  useEffect(() => {
    if (spaceIdFromUrl) {
      setEditValues((prev) => ({
        ...prev,
        spaceId: spaceIdFromUrl,
      }));
    }
  }, [spaceIdFromUrl]);

  const handleChange = (field: keyof CreateRoomRequest, value: any) => {
    if (editValues[field] !== value) {
      setEditValues((prevValues) => {
        const updatedValues = { ...prevValues, [field]: value };
        setHasChanges(true);
        return updatedValues;
      });
    }
  };


  const handleAddAmenity = () => {
    if (!newAmenity.amenityType.trim()) return;
    const updatedAmenities = [...editValues.amenities, { ...newAmenity, details: newAmenity.details?.trim() || '' }];
    setEditValues((prev) => ({ ...prev, amenities: updatedAmenities }));
    setNewAmenity({ amenityType: '', details: '', pricePerH: 0  });
    setHasChanges(true);
  };

  const handleAddGear = () => {
    if (!newGear.gearType.trim()) return;
    const updatedGears = [...editValues.gears, { ...newGear, details: newGear.details?.trim() || '' }];
    setEditValues((prev) => ({ ...prev, gears: updatedGears }));
    setNewGear({ gearType: '', details: '', pricePerH: 0  });
    setHasChanges(true);
  };

  const handleDeleteGear = (index: number) => {
    const updatedGears = editValues.gears.filter((_, i) => i !== index);
    setEditValues((prev) => ({ ...prev, gears: updatedGears }));
    setHasChanges(true);
  };

  const handleDeleteAmenity = (index: number) => {
    const updatedAmenities = editValues.amenities.filter((_, i) => i !== index);
    setEditValues((prev) => ({ ...prev, amenities: updatedAmenities }));
    setHasChanges(true);
  };

  const handleToggleCategory = (category: string) => {
    const currentCategories = editValues.categories || [];
    let updatedCategories: string[];
    
    if (currentCategories.includes(category)) {
      updatedCategories = currentCategories.filter((c) => c !== category);
      // If removing the primary category, clear it
      if (editValues.primaryCategory === category) {
        setEditValues((prev) => ({
          ...prev,
          categories: updatedCategories,
          primaryCategory: updatedCategories.length > 0 ? updatedCategories[0] : undefined
        }));
      } else {
        setEditValues((prev) => ({ ...prev, categories: updatedCategories }));
      }
    } else {
      updatedCategories = [...currentCategories, category];
      setEditValues((prev) => ({
        ...prev,
        categories: updatedCategories,
        primaryCategory: prev.primaryCategory || category // Auto-select as primary if none selected
      }));
    }
    setHasChanges(true);
  };

  const handleSetPrimaryCategory = (category: string) => {
    setEditValues((prev) => ({ ...prev, primaryCategory: category }));
    setHasChanges(true);
  };

  const handleRemoveCategory = (category: string, event: React.MouseEvent) => {
    event.stopPropagation();
    handleToggleCategory(category);
  };

  const handleSelectAllCategories = () => {
    const allCategories = Object.keys(categoryIcons);
    const currentCategories = editValues.categories || [];
    if (currentCategories.length === allCategories.length) {
      // Deselect all
      setEditValues((prev) => ({
        ...prev,
        categories: [],
        primaryCategory: undefined
      }));
    } else {
      // Select all
      setEditValues((prev) => ({
        ...prev,
        categories: allCategories,
        primaryCategory: prev.primaryCategory && allCategories.includes(prev.primaryCategory)
          ? prev.primaryCategory
          : allCategories[0]
      }));
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    setHasSubmitted(true); // Mark that user tried to submit
  
    if (
      !editValues.name.trim() ||
      !editValues.description.trim() ||
      editValues.area <= 0 ||
      editValues.pricePerH <= 0
    ) {
      setErrorMessage(t('room.fillAllRequiredFields', 'Please fill out all required fields: Room Name, Description, Room Area, and Price.'));
      return;
    }
  
    try {
      await hostApiClient.createRoom(editValues);
      navigate('/host/spaces');
    } catch (err: any) {
      console.error(t('room.failedToCreateRoom', 'Failed to create room:'), err);
      if (err?.response?.data?.error) {
        setErrorMessage(err.response.data.error);
      } else {
        setErrorMessage(t('room.unexpectedError', 'An unexpected error occurred. Please try again.'));
      }
    }
  };
  
  

  const handleCancel = () => {
    navigate('/host/spaces');
  };

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1000, width: '100%', mx: 'auto' }}>
        <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" component="h1" gutterBottom>{t('room.createNew', 'Create New Room')}</Typography>
          <Divider sx={{ mb: { xs: 2, md: 3 } }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
              <TextField
                select
                label={t('room.space', 'Space')}
                value={editValues.spaceId}
                onChange={(e) => handleChange('spaceId', e.target.value)}
                fullWidth
                disabled={!!spaceIdFromUrl} // ⛔ prevent editing if spaceId is in URL
              >
                {spaces.map((space) => (
                  <MenuItem key={space.id} value={space.id}>{space.name}</MenuItem>
                ))}
              </TextField>

              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editValues.visible !== false}
                      onChange={(e) => handleChange('visible', e.target.checked)}
                      inputProps={{
                        'aria-label': t('rooms.createVisible', 'Visible on public space page'),
                      }}
                    />
                  }
                  label={t('rooms.createVisible', 'Visible on public space page')}
                />
                <FormHelperText sx={{ ml: 0 }}>
                  {t(
                    'rooms.createVisibleHelper',
                    'Hidden rooms stay in your calendar and host tools, but are omitted from public discovery and new bookings.',
                  )}
                </FormHelperText>
              </Grid>
              
              {/* Categories Section */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1 }}>
                    {t('room.categories', 'Room Categories')}
                  </InputLabel>
                  <Box
                    onClick={(e) => setCategoryAnchorEl(e.currentTarget)}
                    sx={{
                      mt: 2,
                      minHeight: 56,
                      border: '1px solid',
                      borderColor: categoryAnchorEl ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(79, 95, 235, 0.04)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                      {editValues.categories && editValues.categories.length > 0 ? (
                        <>
                          {[...editValues.categories]
                            .sort((a, b) => {
                              // Primary category first
                              if (a === editValues.primaryCategory) return -1;
                              if (b === editValues.primaryCategory) return 1;
                              return 0;
                            })
                            .slice(0, 3)
                            .map((category) => {
                              const isPrimary = editValues.primaryCategory === category;
                              const color = isPrimary ? '#4F5FEB' : '#58a7b9';
                              return (
                                <Chip
                                  key={category}
                                  label={getCategoryLabel(category)}
                                  size="medium"
                                  onDelete={(e) => handleRemoveCategory(category, e)}
                                  deleteIcon={<CloseIcon sx={{ fontSize: '1rem' }} />}
                                  sx={{
                                    height: 32,
                                    bgcolor: color,
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '16px',
                                    fontWeight: isPrimary ? 700 : 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    fontSize: '0.75rem',
                                    '& .MuiChip-label': {
                                      fontSize: '0.75rem',
                                      px: 1.5,
                                      fontWeight: 'inherit',
                                    },
                                    '& .MuiChip-deleteIcon': {
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      fontSize: '1rem',
                                      '&:hover': {
                                        color: 'white',
                                      },
                                    },
                                  }}
                                />
                              );
                            })}
                          {editValues.categories.length > 3 && (
                            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                              +{editValues.categories.length - 3}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('room.selectCategories', 'Select categories...')}
                        </Typography>
                      )}
                    </Box>
                    {categoryAnchorEl ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </Box>
                  <FormHelperText>
                    {t('room.categoriesDescription', 'Select one or more categories that describe this room')}
                  </FormHelperText>
                </FormControl>

                <Popover
                  open={Boolean(categoryAnchorEl)}
                  anchorEl={categoryAnchorEl}
                  onClose={() => setCategoryAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        width: categoryAnchorEl?.offsetWidth || 300,
                        mt: 0.5,
                        maxHeight: 400,
                        borderRadius: 2,
                      },
                    },
                  }}
                >
                  <Box sx={{ p: 1 }}>
                    {/* Select All */}
                    <ListItemButton
                      onClick={handleSelectAllCategories}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Checkbox
                          checked={editValues.categories?.length === Object.keys(categoryIcons).length}
                          indeterminate={
                            (editValues.categories?.length || 0) > 0 &&
                            (editValues.categories?.length || 0) < Object.keys(categoryIcons).length
                          }
                          sx={{
                            color: 'text.secondary',
                            '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                              color: '#4F5FEB',
                            },
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('common.selectAll', 'Select all')}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>

                    <Divider sx={{ my: 1 }} />

                    {/* Individual Categories */}
                    {Object.keys(categoryIcons).map((category) => {
                      const isSelected = editValues.categories?.includes(category);
                      const isPrimary = editValues.primaryCategory === category;

                      return (
                        <ListItemButton
                          key={category}
                          onClick={() => handleToggleCategory(category)}
                          sx={{
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              checked={isSelected}
                              sx={{
                                color: 'text.secondary',
                                '&.Mui-checked': {
                                  color: '#4F5FEB',
                                },
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={getCategoryLabel(category)}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                            }}
                          />
                          {isSelected && (
                            <Radio
                              checked={isPrimary}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetPrimaryCategory(category);
                              }}
                              size="small"
                              sx={{
                                color: 'text.secondary',
                                '&.Mui-checked': {
                                  color: '#4F5FEB',
                                },
                              }}
                            />
                          )}
                        </ListItemButton>
                      );
                    })}
                  </Box>
                  
                  {editValues.categories && editValues.categories.length > 0 && (
                    <Box sx={{ p: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('room.primaryCategoryDescription', 'Click the radio button to select a primary category - it will be featured in search results')}
                      </Typography>
                    </Box>
                  )}
                </Popover>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={t('room.name', 'Room Name')}
                  value={editValues.name}
                  onChange={(e) => {
                    handleChange('name', e.target.value);
                    setErrorMessage(null);
                  }}
                  fullWidth
                  required
                  error={Boolean(errorMessage) || (hasSubmitted && !editValues.name.trim())}
                  helperText={
                    hasSubmitted && !editValues.name.trim()
                      ? t('room.nameRequired', 'Room name is required')
                      : errorMessage || ' '
                  }                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('room.description', 'Description')}
                  value={editValues.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  fullWidth
                  multiline
                  required
                  rows={10}
                  error={hasSubmitted && !editValues.description.trim()}
                  helperText={
                    hasSubmitted && !editValues.description.trim()
                      ? t('room.descriptionRequired', 'Description is required')
                      : ' '
                  }
                />
              </Grid>
              <Grid item xs={12}>
              <Box display="flex" gap={2} alignItems="flex-start">
                <FormControl error={hasSubmitted && editValues.area <= 0}>
                  <AmountInput
                    amount={editValues.area.toString()}
                    label={t('room.area', 'Room Area')}
                    onAmountChange={(newAmount) => handleChange('area', parseFloat(newAmount) || 0)}
                    onErrorChange={() => {}}
                    currencySymbol={editValues.areaUnit}
                    width={200}
                  />
                  <FormHelperText>
                    {editValues.area <= 0 ? t('room.areaRequired', 'Room area is required') : ' '}
                  </FormHelperText>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>{t('room.unit', 'Unit')}</InputLabel>
                  <Select
                    value={editValues.areaUnit}
                    label={t('room.unit', 'Unit')}
                    onChange={(e) => handleChange('areaUnit', e.target.value)}
                  >
                    <MenuItem value="m2">m²</MenuItem>
                    <MenuItem value="ft2">ft²</MenuItem>
                  </Select>
                </FormControl>
              </Box>

            </Grid>
              <Grid item xs={12}>
                <MediaUploader
                  label={t('room.images', 'Room Images')}
                  initialMedia={editValues.imageUrls || []}
                  onMediaChange={(newMedia) => handleChange('imageUrls', newMedia)}
                />
              </Grid>
            </Grid>

            <Stack direction="column" spacing={4} mt={4} mb={4}>
              {/* Gears Section */}
              <Stack spacing={2}>
                <Typography variant="subtitle1">{t('room.gears', 'Gears')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <FormControl sx={{ minWidth: 200 }} size="small">
                    <Select
                      value={newGear.gearType}
                      onChange={(e) => setNewGear({ ...newGear, gearType: e.target.value })}
                    >
                      {Object.keys(gearIcons).map((type) => (
                        <MenuItem key={type} value={type}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {gearIcons[type]}
                            <span>{getGearLabel(type)}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label={t('room.details', 'Details')}
                    size="small"
                    value={newGear.details}
                    onChange={(e) => setNewGear({ ...newGear, details: e.target.value })}
                  />
                  <AmountInput
                    amount={newGear.pricePerH.toString()}
                    label={t('room.pricePerHour', 'Price per hour')}
                    onAmountChange={(newAmount) => setNewGear({ ...newGear, pricePerH: parseFloat(newAmount) || 0 })}
                    onErrorChange={() => {}}
                    currencySymbol={currencySymbols[currencyCode.toString()] || '$'}
                    width={120}
                  />
                  <Tooltip title={t('room.addGear', 'Add Gear')}>
                    <IconButton color="primary" onClick={handleAddGear}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1, alignItems: 'center' }}>
                    {editValues.gears.map((gear, index) => (
                      <Chip
                        key={index}
                        label={`${getGearLabel(gear.gearType)}${gear.details ? ': ' + gear.details : ''}${gear.pricePerH ? ` - ${gear.pricePerH.toFixed(2)} ${currencyCode}` : ''}`}
                        variant="outlined"
                        size="small"
                        onDelete={() => handleDeleteGear(index)}
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>

              {/* Amenities Section */}
              <Stack spacing={2}>
                <Typography variant="subtitle1">{t('room.amenities', 'Amenities')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <FormControl sx={{ minWidth: 200 }} size="small">
                    <Select
                      value={newAmenity.amenityType}
                      onChange={(e) => setNewAmenity({ ...newAmenity, amenityType: e.target.value })}
                    >
                      {Object.keys(amenityIcons).map((type: keyof typeof amenityIcons) => (
                        <MenuItem key={type} value={type}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {amenityIcons[type]}
                            <span>{getAmenityLabel(type)}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label={t('room.details', 'Details')}
                    size="small"
                    value={newAmenity.details}
                    onChange={(e) => setNewAmenity({ ...newAmenity, details: e.target.value })}
                  />
                  <AmountInput
                    amount={newAmenity.pricePerH.toString()}
                    label={t('room.pricePerHour', 'Price per hour')}
                    onAmountChange={(newAmount) => setNewAmenity({ ...newAmenity, pricePerH: parseFloat(newAmount) || 0 })}
                    onErrorChange={() => {}}
                    currencySymbol={currencySymbols[currencyCode.toString()] || '$'}
                    width={120}
                  />
                  <Tooltip title={t('room.addAmenity', 'Add Amenity')} placement="left">
                    <IconButton color="primary" onClick={handleAddAmenity}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1, alignItems: 'center' }}>
                    {editValues.amenities.map((amenity, index) => (
                      <Chip
                        key={index}
                        label={`${getAmenityLabel(amenity.amenityType)}${amenity.details ? ': ' + amenity.details : ''}${amenity.pricePerH ? ` - ${amenity.pricePerH.toFixed(2)} ${currencyCode}` : ''}`}
                        variant="outlined"
                        size="small"
                        onDelete={() => handleDeleteAmenity(index)}
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>

              {/* Google Calendar Integration */}
              {GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED ? (
                <Stack spacing={2}>
                  <Typography variant="subtitle1">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarTodayIcon fontSize="small" />
                      {t('room.googleCalendarIntegration', 'Google Calendar Integration')}
                    </Box>
                  </Typography>
                  
                  {googleCalendarOptionsLoading ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        {t('room.loadingGoogleCalendars', 'Loading Google Calendars...')}
                      </Typography>
                    </Box>
                  ) : googleCalendarOptions.length === 0 ? (
                    <Box
                      sx={{
                        backgroundColor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('room.googleCalendarNotEnabled', 'Google Calendar integration is not enabled for your organization.')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('room.enableGoogleCalendar', 'To sync room bookings with Google Calendar, enable it under Integrations → Google Calendar.')}
                      </Typography>
                      <Link
                        href={ORGANIZATION_GOOGLE_CALENDAR_HREF}
                        variant="body2"
                        color="primary"
                        sx={{ textDecoration: 'none', fontWeight: 'medium' }}
                      >
                        {t('room.goToOrganizationSettings', 'Go to Organization Settings')} →
                      </Link>
                    </Box>
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('room.selectGoogleCalendar', 'Select Google Calendar')}</InputLabel>
                      <Select
                        value={editValues.googleCalendarInfo?.id || ''}
                        label={t('room.selectGoogleCalendar', 'Select Google Calendar')}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedOption = googleCalendarOptions.find(opt => opt.id === selectedId);
                          handleChange('googleCalendarInfo', selectedOption || null);
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('room.noCalendarSelected', 'No calendar selected')}</em>
                        </MenuItem>
                        {googleCalendarOptions.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Stack>
              ) : null}

              {/* Room Color Selection */}
              <Stack spacing={2}>
                <Typography variant="subtitle1">
                  {t('room.color', 'Room Color')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {ROOM_CALENDAR_COLOR_OPTIONS.map((colorOption) => {
                    const isSelected = editValues.color === colorOption.color;
                    return (
                      <Box
                        key={colorOption.color}
                        onClick={() => handleChange('color', colorOption.color)}
                        sx={{
                          width: 48,
                          height: 48,
                          backgroundColor: colorOption.color,
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: isSelected ? '4px solid' : '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          position: 'relative',
                          boxShadow: isSelected 
                            ? '0 0 0 3px rgba(25, 118, 210, 0.2), 0 4px 8px rgba(0, 0, 0, 0.2)' 
                            : 'none',
                          transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                          '&:hover': {
                            transform: 'scale(1.15)',
                            boxShadow: isSelected 
                              ? '0 0 0 3px rgba(25, 118, 210, 0.3), 0 6px 12px rgba(0, 0, 0, 0.3)' 
                              : '0 2px 8px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                        title={t(`room.colorNames.${colorOption.key}`, colorOption.key)}
                      >
                        {isSelected && (
                          <CheckIcon 
                            sx={{ 
                              color: 'white', 
                              fontSize: 28,
                              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))',
                              stroke: 'black',
                              strokeWidth: 0.5,
                            }} 
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {t('room.colorDescription', 'This color will be used to display room events in the calendar')}
                </Typography>
              </Stack>
            </Stack>

            <Grid item xs={12}>
              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f0f8ff',
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: 3,
                  p: 3,
                  mt: 2,
                })}
              >
                <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                  💰 {t('room.setRoomPricing', 'Set Room Pricing')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    mb: 2,
                    color: theme.palette.text.secondary,
                  })}
                >
                  {t('room.basePriceInfo', 'This is the base hourly rate for booking this room. Add-ons like gear and amenities will be extra.')}
                </Typography>
                <FormControl error={hasSubmitted && editValues.area <= 0}>
                  <AmountInput
                    amount={editValues.pricePerH.toString()}
                    label={t('room.pricePerHour', 'Price per hour')}
                    onAmountChange={(newAmount) => handleChange('pricePerH', parseFloat(newAmount) || 0)}
                    onErrorChange={() => {}}
                    currencySymbol={currencySymbols[currencyCode.toString()] || '$'}
                    width={120}
                  />
                  <FormHelperText>
                    {editValues.pricePerH <= 0 ? t('room.priceRequired', 'Price is required') : ' '}
                  </FormHelperText>
                </FormControl>
              </Box>
            </Grid>

          <Box display="flex" gap={2} mt={4} justifyContent="flex-end">
            <Button 
              variant="contained" 
              onClick={handleSave} 
              disabled={
                !hasChanges ||
                !editValues.name.trim() ||
                !editValues.description.trim() ||
                editValues.area <= 0 ||
                editValues.pricePerH <= 0
              }
            >
              {t('common.save', 'Save')}
            </Button>
            <Button variant="outlined" onClick={handleCancel}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </Box>
        </Card>
      </Box>
    </>
  );
};

export default CreateRoomPage;
